import { useState, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { useBulkAddCompanyByDomainMutation } from '../../services/organizationsV2';
import { activeNetworkSelector } from '../../redux/selectors';
import { trackEvent } from '../../helpers/analytics';

const BATCH_SIZE = 20;

const chunkArray = (arr, size) => {
  const chunks = [];
  for (let i = 0; i < arr.length; i += size) {
    chunks.push(arr.slice(i, i + size));
  }
  return chunks;
};

const useBulkAddCompaniesQueue = () => {
  const [bulkAdd] = useBulkAddCompanyByDomainMutation();
  const activeNetwork = useSelector(activeNetworkSelector);
  const [state, setState] = useState({
    isLoading: false,
    progress: { completed: 0, total: 0, processedDomains: 0, totalDomains: 0 },
    meta: null,
    error: null,
  });

  const execute = useCallback(
    async (allDomains) => {
      const chunks = chunkArray(allDomains, BATCH_SIZE);
      const accumulated = { created: 0, published: 0, failed: [], notActive: [] };

      const totalDomains = allDomains.length;
      setState({
        isLoading: true,
        progress: { completed: 0, total: chunks.length, processedDomains: 0, totalDomains },
        meta: null,
        error: null,
      });

      try {
        /* eslint-disable no-await-in-loop */
        // Process chunks sequentially to maintain order and progress tracking
        for (let index = 0; index < chunks.length; index += 1) {
          const chunk = chunks[index];
          try {
            const result = await bulkAdd({ networkId: activeNetwork.id, domains: chunk }).unwrap();
            accumulated.created += result.meta.created;
            accumulated.published += result.meta.published;
            accumulated.failed.push(...result.meta.failed);
            accumulated.notActive.push(...result.meta.notActive);
          } catch (err) {
            accumulated.failed.push(...chunk.map((d) => ({ domain: d, error: 'Request failed' })));
          }
          setState((prev) => ({
            ...prev,
            progress: {
              ...prev.progress,
              completed: index + 1,
              processedDomains: prev.progress.processedDomains + chunk.length,
            },
          }));
        }

        const { created, published, failed, notActive } = accumulated;
        trackEvent('add_employer:employer_added', {
          network_id: activeNetwork.id,
          total_domains: failed.length + created + published + notActive.length,
          added_domains_qa_true: published,
          added_domains_qa_false: created,
          not_added_domains: failed.length + notActive.length,
        });

        failed.forEach((item) => {
          trackEvent('add_employer:failed', {
            network_id: activeNetwork.id,
            ...item,
          });
        });

        notActive.forEach((domain) => {
          trackEvent('add_employer:failed', {
            network_id: activeNetwork.id,
            domain,
            error: 'The organization has been added but it is not active',
          });
        });

        setState({
          isLoading: false,
          progress: { completed: chunks.length, total: chunks.length, processedDomains: totalDomains, totalDomains },
          meta: accumulated,
          error: null,
        });
      } catch (err) {
        setState({
          isLoading: false,
          progress: { completed: 0, total: 0, processedDomains: 0, totalDomains: 0 },
          meta: null,
          error: err,
        });
      }
    },
    [bulkAdd, activeNetwork.id],
  );

  const reset = useCallback(() => {
    setState({
      isLoading: false,
      progress: { completed: 0, total: 0, processedDomains: 0, totalDomains: 0 },
      meta: null,
      error: null,
    });
  }, []);

  return { execute, reset, ...state };
};

export default useBulkAddCompaniesQueue;
